import { useEffect, useRef, type RefObject } from "react";
import { ProvetDialogStep } from "../types/dialogSteps";
import { SCROLL_CONFIG } from "../utilities/provetConstants";
import {
  scrollElementToTop,
  scrollToDialog,
  scrollToElement
} from "../utilities/scrollUtilities";

interface ScrollManagementState {
  formRef: RefObject<HTMLFormElement>;
  confirmationModalRef: RefObject<HTMLDivElement>;
  confirmationViewRef: RefObject<HTMLDivElement>;
  treatmentModalRef: RefObject<HTMLDivElement>;
  treatmentModalContentRef: RefObject<HTMLDivElement>;
  treatmentScrollIndicatorRef: RefObject<HTMLDivElement>;
  summaryModalContentRef: RefObject<HTMLDivElement>;
  summaryScrollIndicatorRef: RefObject<HTMLDivElement>;
  cancelModalContentRef: RefObject<HTMLDivElement>;
  cancelScrollIndicatorRef: RefObject<HTMLDivElement>;
  bookingSuccessModalContentRef: RefObject<HTMLDivElement>;
  bookingFailureModalContentRef: RefObject<HTMLDivElement>;
}

interface UseProvetScrollManagementOptions {
  sessionId: string;
  activeStep?: ProvetDialogStep;
  /** True once a clinic or treatment preselected from the URL has been found */
  hasSyncedPreselections?: boolean;
}

/**
 * Smooth scrolling, unless the customer prefers reduced motion.
 */
const getPreferredScrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

/**
 * Whether to scroll to the form when the page loads. Not when the URL points at an anchor,
 * nor when the browser restores the scroll position (Back/Forward or reload), nor when the
 * page has already been scrolled.
 */
const shouldScrollOnPageLoad = (): boolean => {
  if (window.location.hash) {
    return false;
  }

  const navigation = performance.getEntriesByType?.("navigation")[0] as
    PerformanceNavigationTiming | undefined;

  if (navigation?.type === "back_forward" || navigation?.type === "reload") {
    return false;
  }

  return window.scrollY === 0;
};

/**
 * Custom hook to manage scroll behavior and refs for the Provet booking form.
 * Handles initial scroll, step-based scrolling, and provides refs for all scrollable elements.
 */
export const useProvetScrollManagement = ({
  sessionId,
  activeStep,
  hasSyncedPreselections = false
}: UseProvetScrollManagementOptions): ScrollManagementState => {
  // Track initial render to prevent unwanted scroll on activeStep change
  const hasInitialScrolledRef = useRef<boolean>(false);
  // Track whether we've already handled the post-preselection scroll
  const hasHandledPreselectionScrollRef = useRef<boolean>(false);
  // Whether the page may be scrolled to the form for the page load (and the preselection)
  const isPageLoadScrollAllowedRef = useRef<boolean>(false);

  // Refs for form and modals
  const formRef = useRef<HTMLFormElement>(null);
  const confirmationModalRef = useRef<HTMLDivElement>(null);
  const confirmationViewRef = useRef<HTMLDivElement>(null);
  const treatmentModalRef = useRef<HTMLDivElement>(null);
  const treatmentModalContentRef = useRef<HTMLDivElement>(null);
  const treatmentScrollIndicatorRef = useRef<HTMLDivElement>(null);
  const summaryModalContentRef = useRef<HTMLDivElement>(null);
  const summaryScrollIndicatorRef = useRef<HTMLDivElement>(null);
  const cancelModalContentRef = useRef<HTMLDivElement>(null);
  const cancelScrollIndicatorRef = useRef<HTMLDivElement>(null);
  const bookingSuccessModalContentRef = useRef<HTMLDivElement>(null);
  const bookingFailureModalContentRef = useRef<HTMLDivElement>(null);

  // Scrolls to the top of the form, where the important information (Step 0) is shown.
  // Step 0 has no step id of its own, and is hidden on large screens, so the form is used.
  const scrollToFormTop = () => {
    if (formRef.current) {
      scrollElementToTop(
        formRef.current,
        SCROLL_CONFIG.DIALOG_OFFSET,
        getPreferredScrollBehavior()
      );
    }
  };

  // Initial scroll to the top of the form
  useEffect(() => {
    // Mark that initial scroll has completed
    const timeout = setTimeout(() => {
      hasInitialScrolledRef.current = true;
    }, 100);

    isPageLoadScrollAllowedRef.current = shouldScrollOnPageLoad();

    if (!isPageLoadScrollAllowedRef.current) {
      return () => clearTimeout(timeout);
    }

    scrollToFormTop();

    // Images and fonts above the form can still move it until the page has loaded. Scroll
    // once more then, unless the customer has started scrolling or using the page.
    let customerHasInteracted = false;
    const interactionEvents = ["wheel", "touchstart", "keydown", "pointerdown"];
    const onInteraction = () => {
      customerHasInteracted = true;
    };
    const onLoad = () => {
      if (!customerHasInteracted) {
        scrollToFormTop();
      }
    };

    interactionEvents.forEach(type =>
      window.addEventListener(type, onInteraction, { passive: true })
    );
    if (document.readyState !== "complete") {
      window.addEventListener("load", onLoad, { once: true });
    }

    return () => {
      clearTimeout(timeout);
      interactionEvents.forEach(type => window.removeEventListener(type, onInteraction));
      window.removeEventListener("load", onLoad);
    };
  }, [sessionId]);

  // Once a clinic or treatment from the URL has been found (after the lists have loaded),
  // scroll to the top of the form again so the customer sees it. Only once, and only if
  // the customer hasn't moved on from the first step yet.
  useEffect(() => {
    if (!hasSyncedPreselections || hasHandledPreselectionScrollRef.current) {
      return;
    }

    hasHandledPreselectionScrollRef.current = true;

    if (
      !isPageLoadScrollAllowedRef.current ||
      activeStep !== ProvetDialogStep.SelectClinic
    ) {
      return;
    }

    requestAnimationFrame(() => {
      setTimeout(scrollToFormTop, 50);
    });
    // Runs when the preselection is found, not on step changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSyncedPreselections]);

  // Scroll to active step when it changes
  // BUT only after the initial scroll has completed
  useEffect(() => {
    // Don't scroll on activeStep changes until after initial scroll is done
    if (!hasInitialScrolledRef.current) {
      return;
    }

    if (activeStep !== undefined) {
      // Skip scrolling for the Confirmation step (Step 5) - it's a modal, not a step in the form
      if (activeStep === ProvetDialogStep.Confirmation) {
        return;
      }

      // Handle Confirmed step (Step 6) separately - scroll to the confirmation view after it renders
      if (activeStep === ProvetDialogStep.Confirmed) {
        // Wait for React to render the confirmation view, then scroll to it
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (confirmationViewRef.current) {
              const elementId = `${sessionId}_Confirmed`;
              scrollToElement(`#${elementId}`, 140, "smooth");
            } else {
              console.warn("Confirmation view ref is not available for scrolling");
            }
          }, 150);
        });
        return;
      }

      // Step 0 has no element of its own: it's the top of the form
      if (activeStep === ProvetDialogStep.ImportantInformation) {
        requestAnimationFrame(() => {
          setTimeout(scrollToFormTop, 50);
        });
        return;
      }

      const elementId = `${sessionId}_${activeStep}`;

      // Use requestAnimationFrame to ensure DOM has been updated,
      // then add a small delay to be absolutely sure React has finished rendering
      requestAnimationFrame(() => {
        setTimeout(() => {
          scrollToDialog(elementId, 140);
        }, 50);
      });
    }
  }, [activeStep, sessionId]);

  return {
    formRef,
    confirmationModalRef,
    confirmationViewRef,
    treatmentModalRef,
    treatmentModalContentRef,
    treatmentScrollIndicatorRef,
    summaryModalContentRef,
    summaryScrollIndicatorRef,
    cancelModalContentRef,
    cancelScrollIndicatorRef,
    bookingSuccessModalContentRef,
    bookingFailureModalContentRef
  };
};
